<template>
  <ComDialog
    :isShow="dialogVisible"
    :width="500"
    title="设置分类"
    alignCenter
    @close="handleClose"
  >
    <div class="set-category-container">
      <!-- 应用选择区 -->
      <div class="section">
        <h3 class="section-title">已选择应用</h3>
        <div class="selected-apps">
          <el-tag
            v-for="app in selectedApps"
            :key="app.id"
            :closable="selectedApps.length > 1"
            @close="handleRemoveApp(app)"
            style="margin-right: 8px; margin-bottom: 8px"
          >
            {{ app.appName }}
          </el-tag>
          <div v-if="selectedApps.length === 0" class="empty-tip">
            请选择应用
          </div>
        </div>
      </div>

      <!-- 分类选择区 -->
      <div class="section">
        <h3 class="section-title">选择分类</h3>
        <div class="category-selector">
          <el-checkbox-group v-model="selectedCategoryIds">
            <el-checkbox
              v-for="category in categoryList"
              :key="category.id"
              :label="category.id"
              style="margin-right: 16px; margin-bottom: 8px"
            >
              {{ category.tagName }}
            </el-checkbox>
          </el-checkbox-group>
          <div v-if="categoryList.length === 0" class="empty-tip">
            暂无分类数据
          </div>
        </div>
      </div>
    </div>

    <!-- 底部按钮 -->
    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button
        type="primary"
        :loading="loading"
        :disabled="loading || selectedApps.length === 0 || selectedCategoryIds.length === 0"
        @click="handleSubmit"
      >
        确定
      </el-button>
    </span>
  </ComDialog>
</template>

<script setup>
import { ref, onMounted, watch, defineEmits } from 'vue';
import ComDialog from '@/components/comDialog/index.vue';
import { ElMessage } from 'element-plus';
import request from '@/utils/request';
import API_CONFIG from '../api.json';

// Props
const props = defineProps({
  // 外部传入的已选择应用数据
  apps: {
    type: Array,
    default: () => []
  }
});

// Emits
const emit = defineEmits(['close', 'success']);

// 弹窗可见性
const dialogVisible = ref(false);

// 加载状态
const loading = ref(false);

// 已选择的应用列表
const selectedApps = ref([]);

// 分类列表
const categoryList = ref([]);

// 已选择的分类ID列表
const selectedCategoryIds = ref([]);

// 监听外部传入的apps变化
watch(() => props.apps, (newApps) => {
  if (newApps && newApps.length > 0) {
    selectedApps.value = [...newApps];
  }
}, { deep: true });

// 打开弹窗
const open = (apps) => {
  if (apps && apps.length > 0) {
    selectedApps.value = [...apps];
  }
  if (selectedApps.value.length === 1) {
   selectedCategoryIds.value = (apps[0].tags||[]).map(tag => tag.id || [])
  }
  dialogVisible.value = true;
  // 加载分类列表
  loadCategoryList();
};

// 暴露open方法给外部调用
defineExpose({
  open
});

// 关闭弹窗
const handleClose = () => {
  dialogVisible.value = false;
  // 重置选择
  selectedCategoryIds.value = [];
  emit('close');
};

// 移除已选择的应用
const handleRemoveApp = (app) => {
  const index = selectedApps.value.findIndex(a => a.id === app.id);
  if (index > -1) {
    selectedApps.value.splice(index, 1);
  }
};

// 加载分类列表
const loadCategoryList = async () => {
  try {
    const res = await request.get(API_CONFIG.queryLabel.url);
    if (res.code === 200) {
      categoryList.value = res.data || [];
    } else {
      ElMessage.error('获取分类列表失败：' + res.msg);
    }
  } catch (error) {
    console.error('获取分类列表失败：', error);
    ElMessage.error('获取分类列表失败');
  }
};

// 提交设置
const handleSubmit = async () => {
  if (selectedApps.value.length === 0) {
    ElMessage.warning('请选择应用');
    return;
  }
  
  if (selectedCategoryIds.length === 0) {
    ElMessage.warning('请选择分类');
    return;
  }

  try {
    loading.value = true;
    
    // 准备请求数据
    const appIds = selectedApps.value.map(app => app.id);
    
    // 调用API为应用添加分类
    const res = await request.post(API_CONFIG.addTagsToApps.url, {
      appIds: appIds,
      tagIds: selectedCategoryIds.value
    });
    
    if (res.code === 200) {
      ElMessage.success('设置分类成功');
      handleClose();
      emit('success');
    } else {
      ElMessage.error('设置分类失败：' + res.msg);
    }
  } catch (error) {
    console.error('设置分类失败：', error);
  } finally {
    loading.value = false;
  }
};

// 组件挂载时加载分类列表
onMounted(() => {
  loadCategoryList();
});
</script>

<style lang="scss" scoped>
.section {
  margin-bottom: 20px;
  
  .section-title {
    font-size: 16px;
    font-weight: 500;
    margin-bottom: 16px;
    color: #303133;
  }
  
  .empty-tip {
    color: #909399;
    padding: 20px 0;
  }
}

.selected-apps {
  display: flex;
  flex-wrap: wrap;
}

.category-selector {
  display: flex;
  flex-wrap: wrap;
}

.dialog-footer {
  display: flex;
  justify-content: center;
  padding-top: 20px;
  border-top: 1px solid #ebeef5;
}
</style>